import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { formatDelta } from '../utils/formatDelta';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useSeason } from '@/contexts/SeasonContext';
import { PaymentDialog } from '@/components/PaymentInstructions';
import { Reveal } from './Reveal';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// Keep rows whose season is unknown so an API that predates season stamping still renders.
const seasonMatches = (year, activeSeason) => {
  if (activeSeason == null) return true;
  if (year === null || year === undefined || year === '') return true;
  return Number(year) === activeSeason;
};

const inSeason = (row, activeSeason) => seasonMatches(row?.season_year, activeSeason);

export function Dashboard({ token, me, onLogout }) {
  const { activeSeason, availableSeasons, nowIso: siteNowISO, entriesOpen, entryFee, dataVersion, refresh: refreshSeason } = useSeason();
  const [error] = useState('');
  const [globalToast] = useState('');
  const [week, setWeek] = useState(null);
  // Dashboard entries summary removed; entries management lives on /entries page
  const [siteNowFetchedAt, setSiteNowFetchedAt] = useState(() => Date.now());
  const [hasEntries, setHasEntries] = useState(null); // null = unknown, boolean once loaded
  const [needsPickCount, setNeedsPickCount] = useState(0); // how many paid alive entries still lack a current-week pick
  const [submittedPickCount, setSubmittedPickCount] = useState(0); // paid alive entries that already have a current-week pick
  const [unpaidCount, setUnpaidCount] = useState(0); // active-season entries that still need payment
  const [isReturningUser, setIsReturningUser] = useState(false); // prior-season entry exists
  const [weeksList, setWeeksList] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState('');
  const [initialWeekSet, setInitialWeekSet] = useState(false);
  // removed unused entryEdits state (rename uses prompt)
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate('/auth');
  }, [token, navigate]);

  const parseJsonSafe = async (res) => {
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const text = await res.text();
      throw new Error(`Unexpected content-type (${ct}): ${text.slice(0, 120)}`);
    }
    return res.json();
  };

  // entry/pick helpers removed from Dashboard; entries management lives on /entries

  // listEntries removed from Dashboard to reduce initial fan-out; entries management lives on /entries

  // `?season=` is omitted until the server tells us which season is active.
  const seasonQuery = activeSeason == null ? '' : `?season=${encodeURIComponent(activeSeason)}`;

  const loadWeekTeams = useCallback(async () => {
    try {
      const wRes = await fetch(`${API_BASE}/api/weeks/current`);
      const wData = await parseJsonSafe(wRes);
      if (wRes.ok) setWeek(wData);
    } catch {}
  }, []);

  const loadAllWeeks = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/weeks${seasonQuery}`);
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) {
        setWeeksList(data.filter(w => inSeason(w, activeSeason)));
      }
    } catch {}
  }, [seasonQuery, activeSeason]);

  // The poll hands back a fresh `week` object every tick; depending on the fields instead of
  // the object keeps the entries fetch tied to an actual week change.
  const currentWeekId = week?.id ?? null;
  const currentWeekSeason = week?.season_year ?? null;

  const loadHasEntries = useCallback(async () => {
    if (!token || !me?.id) return;
    try {
      // Prefer combined endpoint so we can also check current-week pick status
      let list = null;
      const combinedRes = await fetch(`${API_BASE}/api/users/${encodeURIComponent(me.id)}/entries-with-picks${seasonQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (combinedRes.ok) {
        const combinedData = await parseJsonSafe(combinedRes).catch(() => null);
        if (Array.isArray(combinedData)) list = combinedData;
      }
      if (!list) {
        const res = await fetch(`${API_BASE}/api/users/${encodeURIComponent(me.id)}/entries${seasonQuery}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await parseJsonSafe(res).catch(() => []);
        if (!res.ok || !Array.isArray(data)) return;
        list = await Promise.all(data.map(async (e) => {
          try {
            const pRes = await fetch(`${API_BASE}/api/entries/${encodeURIComponent(e.id)}/picks`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const picks = pRes.ok ? await parseJsonSafe(pRes).catch(() => []) : [];
            return { ...e, picks: Array.isArray(picks) ? picks : [] };
          } catch {
            return { ...e, picks: [] };
          }
        }));
      }

      const scoped = list.filter(e => inSeason(e, activeSeason));
      setHasEntries(scoped.length > 0);
      setUnpaidCount(scoped.filter(e => !e.is_paid).length);
      const weekId = currentWeekId;
      // A current week belonging to a finished season can't create pick obligations.
      if (!weekId || !seasonMatches(currentWeekSeason, activeSeason)) {
        setNeedsPickCount(0);
        setSubmittedPickCount(0);
        return;
      }
      const paidAlive = scoped.filter(e => !!e.is_paid && !e.is_eliminated);
      const withPick = paidAlive.filter(e =>
        Array.isArray(e.picks) && e.picks.some(p => p && String(p.week_id) === String(weekId) && p.team_abbr)
      );
      setNeedsPickCount(paidAlive.length - withPick.length);
      setSubmittedPickCount(withPick.length);
    } catch {}
  }, [token, me?.id, currentWeekId, currentWeekSeason, seasonQuery, activeSeason]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      await Promise.all([loadWeekTeams(), loadAllWeeks()]);
    })();
  }, [token, loadWeekTeams, loadAllWeeks, me, dataVersion]);

  useEffect(() => {
    loadHasEntries();
  }, [loadHasEntries, dataVersion]);

  // Lazy: only when the no-entries banner would show, check prior seasons for welcome copy.
  useEffect(() => {
    if (!token || !me?.id || !entriesOpen || hasEntries !== false) {
      setIsReturningUser(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/${encodeURIComponent(me.id)}/entries?season=all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await parseJsonSafe(res).catch(() => []);
        if (cancelled || !res.ok || !Array.isArray(data)) return;
        const hasPrior = data.some(e => {
          const y = e?.season_year;
          if (y === null || y === undefined || y === '') return false;
          return activeSeason == null || Number(y) !== activeSeason;
        });
        setIsReturningUser(hasPrior);
      } catch {
        if (!cancelled) setIsReturningUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, me?.id, entriesOpen, hasEntries, activeSeason]);

  useEffect(() => {
    if (siteNowISO) setSiteNowFetchedAt(Date.now());
  }, [siteNowISO]);

  // Live countdown clock — tick every second
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Advance from last site-time snapshot so countdown ticks even when siteNowISO is set.
  const effectiveNowMs = useMemo(() => {
    if (siteNowISO) {
      const base = new Date(siteNowISO).getTime();
      if (Number.isFinite(base)) return base + (nowTs - siteNowFetchedAt);
    }
    return nowTs;
  }, [siteNowISO, siteNowFetchedAt, nowTs]);

  const sortedWeeks = useMemo(() => {
    return [...(weeksList || [])].sort((a, b) => {
      if (a.season_year !== b.season_year) {
        return b.season_year - a.season_year;
      }
      return b.week_number - a.week_number;
    });
  }, [weeksList]);

  // Default View week to the most recently available (current) week.
  // When the current week belongs to a past season it isn't listed, so fall back to the newest active-season week.
  useEffect(() => {
    if (initialWeekSet || sortedWeeks.length === 0) return;
    const currentListed = week && sortedWeeks.some(w => String(w.id) === String(week.id));
    const next = currentListed ? week.id : sortedWeeks[0]?.id;
    if (next == null) return;
    setSelectedWeekId(String(next));
    setInitialWeekSet(true);
  }, [week, sortedWeeks, initialWeekSet]);

  // If a specific week is selected via the dropdown, show its lock time; otherwise fall back to current week
  const currentLockISO = (() => {
    try {
      if (selectedWeekId) {
        const sel = (weeksList || []).find(w => String(w.id) === String(selectedWeekId));
        return sel && sel.lock_time ? sel.lock_time : '';
      }
    } catch {}
    return week?.lock_time || '';
  })();
  const isLocked = useMemo(() => {
    if (!currentLockISO) return false;
    return effectiveNowMs >= new Date(currentLockISO).getTime();
  }, [effectiveNowMs, currentLockISO]);
  const countdown = useMemo(() => {
    if (!currentLockISO) return '';
    const delta = new Date(currentLockISO).getTime() - effectiveNowMs;
    return delta > 0 ? formatDelta(delta) : '0s';
  }, [effectiveNowMs, currentLockISO]);

  // True while the current week's lock time hasn't passed (used to gate the needs-pick banner)
  const shouldShowEntriesButton = useMemo(() => {
    if (!week || !week.lock_time) return false;
    const currentWeekLocked = effectiveNowMs >= new Date(week.lock_time).getTime();
    return !currentWeekLocked;
  }, [week, effectiveNowMs]);

  const hasPastSeasons = useMemo(
    () => activeSeason != null && (availableSeasons || []).some(y => y !== activeSeason),
    [availableSeasons, activeSeason]
  );

  const showCreateEntryNotice = !!(me && entriesOpen && hasEntries === false);
  // Paid alive entries with no current-week pick yet (only while picks are still open)
  const showNeedsPickBanner = !!(me && needsPickCount > 0 && shouldShowEntriesButton);
  const showUnpaidBanner = !!(me && unpaidCount > 0);
  const isViewingCurrentWeek = !selectedWeekId || String(selectedWeekId) === String(week?.id);
  // Inverse of needs-pick: all paid+alive picks are in, week open, no banner already routing to /entries
  const showUpdatePicks = !!(
    me &&
    submittedPickCount > 0 &&
    needsPickCount === 0 &&
    !showUnpaidBanner &&
    !showNeedsPickBanner &&
    shouldShowEntriesButton &&
    isViewingCurrentWeek
  );

  // removed sortedEntries; Dashboard no longer renders full entries list

  // Week 1 lock gate no longer used in Dashboard UI; removed to prevent unused variable warnings

  // Entry CRUD and pick submission removed from Dashboard

  // removed unused admin week/time helpers (moved to Admin Weeks & Locks)

  if (!token) return <Navigate to="/auth" replace />;

  const weekRevealed = !!(week?.lock_time && effectiveNowMs >= new Date(week.lock_time).getTime());

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 text-left">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-3xl font-semibold tracking-tight">Tears</h1>
          <span className="text-sm text-muted-foreground">South Boston Challenge Club</span>
        </div>
      </div>

      {error && <div className="text-destructive">{error}</div>}

      {week ? (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>
                Season {week.season_year} · Week {week.week_number}
              </CardTitle>
              {weekRevealed ? (
                <Badge variant="success">Revealed</Badge>
              ) : week.lock_time ? (
                <Badge variant="secondary">Open</Badge>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {week.lock_time && (
              <div className="text-muted-foreground">
                Lock time:{' '}
                {(() => {
                  try {
                    return new Date(week.lock_time).toLocaleString(undefined, { timeZone: 'America/New_York' });
                  } catch {
                    return new Date(week.lock_time).toLocaleString();
                  }
                })()}
              </div>
            )}
            {weekRevealed && (
              <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm">
                Current week picks are now revealed below.
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-72" />
          </CardContent>
        </Card>
      )}

      {showCreateEntryNotice && (
        <Card className="border-success/40 bg-success/10">
          <CardContent className="flex flex-wrap items-center gap-3 pt-6">
            <span className="text-sm">
              {isReturningUser ? 'Welcome back!' : 'Welcome to the league!'}{' '}
              You don’t have an entry yet. Create one on the Entries page before the season starts.
            </span>
            <Button asChild>
              <Link to="/entries">Go to Entries →</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {showUnpaidBanner && (
        <Card className="border-warning/40 bg-warning/10">
          <CardContent className="flex flex-wrap items-center gap-3 pt-6">
            <span className="text-sm font-medium">
              {unpaidCount === 1
                ? 'You have an unpaid entry!'
                : `You have ${unpaidCount} unpaid entries!`}{' '}
              Payment is required before {unpaidCount === 1 ? 'it' : 'they'} can submit picks.
            </span>
            <PaymentDialog
              entryFee={entryFee}
              trigger={<Button type="button" variant="secondary">How to pay</Button>}
            />
            <Button asChild variant="secondary">
              <Link to="/entries">{unpaidCount === 1 ? 'View unpaid entry →' : 'View unpaid entries →'}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {showNeedsPickBanner && (
        <Card className="border-warning/40 bg-warning/10">
          <CardContent className="flex flex-wrap items-center gap-3 pt-6">
            <span className="text-sm">
              {needsPickCount === 1
                ? 'You have 1 entry that still needs a pick for this week.'
                : `You have ${needsPickCount} entries that still need a pick for this week.`}
            </span>
            <Button asChild variant="secondary">
              <Link to="/entries">{needsPickCount === 1 ? 'Submit your pick →' : 'Submit your picks →'}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Entries summary removed from Dashboard to reduce initial load; entries management lives on /entries */}

      {/* Manage Entries removed; actions moved inline above */}

      {/* Weekly section (The Grid moved to its own page at /grid) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                View week
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={selectedWeekId || ''}
                  onChange={e => setSelectedWeekId(e.target.value || '')}
                >
                  {sortedWeeks.map(w => (
                    <option key={w.id} value={w.id}>Week {w.week_number} — {w.season_year}</option>
                  ))}
                </select>
              </label>
              {hasPastSeasons && (
                <Link to="/grid" className="text-xs text-muted-foreground underline-offset-4 hover:underline">
                  All seasons on The Grid →
                </Link>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {week && week.lock_time && (
                <div className="reveal-panel inline-block rounded-md px-2.5 py-1.5">
                  <div className="text-sm">
                    {isLocked ? (
                      <>
                        Picks revealed{' '}
                        <Badge variant="outline" className="ml-1.5">locked</Badge>
                      </>
                    ) : (
                      <>
                        Reveal in: <strong className="ml-1.5">{countdown || '—'}</strong>
                      </>
                    )}
                  </div>
                </div>
              )}
              {showUpdatePicks && (
                <Button asChild variant="secondary">
                  <Link to="/entries">Update your picks</Link>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Reveal
            embedded
            preferLatestCompleted
            weekId={selectedWeekId}
            fetchPicks={false}
            showPicksTable={false}
            token={token}
          />
        </CardContent>
      </Card>

      {false && me && me.is_admin && (<div />)}
      {globalToast && <div className="toast" role="status" aria-live="polite">{globalToast}</div>}
    </div>
  );
}
